import { useCallback, useRef, useEffect } from "react";

interface UseLazyLoadPageBlocksArgs {
    isLoading: boolean;
    noRecordsRemaining: boolean;
    onPageNext: () => void;
}

const useLazyLoadPageBlocks = (args: UseLazyLoadPageBlocksArgs) => {
    const { isLoading, noRecordsRemaining, onPageNext } = args;

    const intersectionObserverRef = useRef<IntersectionObserver | null>(null);

    const observerRef = useCallback(
        (node: Element | null) => {
            if (intersectionObserverRef.current != null) {
                intersectionObserverRef.current.disconnect();
                intersectionObserverRef.current = null;
            }

            if (node === null) return;

            if (isLoading || noRecordsRemaining) return;

            intersectionObserverRef.current = new IntersectionObserver(
                (entries: IntersectionObserverEntry[]) => {
                    if (entries.some((e) => e.isIntersecting)) {
                        if (!noRecordsRemaining && !isLoading) {
                            onPageNext();
                        }
                    }
                },
                {
                    root: null,
                    rootMargin: "600px",
                    threshold: 0.1,
                }
            );

            intersectionObserverRef.current.observe(node);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isLoading, noRecordsRemaining, onPageNext]
    );

    useEffect(() => {
        if (noRecordsRemaining && intersectionObserverRef.current) {
            intersectionObserverRef.current.disconnect();
            intersectionObserverRef.current = null;
        }

        return () => {
            if (intersectionObserverRef.current) {
                intersectionObserverRef.current.disconnect();
                intersectionObserverRef.current = null;
            }
        };
    }, [noRecordsRemaining, isLoading]);

    return { observerRef };
};

export default useLazyLoadPageBlocks;
